import { REPORTING_PERIOD_CLOSED } from '@time-tracking/shared';

import { config } from '../config.js';

/**
 * An API error that carries the numeric HTTP status and, when present, the
 * server's `{ error }` message body so the UI can show the real reason
 * (e.g. "Forbidden", "This reporting period is closed") rather than a bare status.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly apiMessage: string | undefined,
    message: string,
    /**
     * The server's machine-readable `{ code }`, when it sends one. Callers branch on this
     * rather than on `apiMessage` so rewording an API message cannot change UI behaviour.
     */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * True when the server refused the request because the week sits inside a closed
 * reporting period. Every such refusal — a contributor's edit, submit or recall, an
 * approver's approve, return or unapprove — carries the one shared code, so callers
 * check this instead of matching the per-action message.
 */
export function isReportingPeriodClosedError(err: unknown): boolean {
  return err instanceof ApiError && err.code === REPORTING_PERIOD_CLOSED;
}

export async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Attempt to read the server's JSON error body; fall back to status text.
    let apiMessage: string | undefined;
    let code: string | undefined;
    try {
      const body = (await response.json()) as { error?: unknown; code?: unknown };
      if (typeof body?.error === 'string') apiMessage = body.error;
      if (typeof body?.code === 'string') code = body.code;
    } catch {
      // Body was empty or not JSON — fall back to the status line below.
    }
    throw new ApiError(
      response.status,
      apiMessage,
      apiMessage ?? `API error: ${response.status} ${response.statusText}`,
      code,
    );
  }
  return response.json() as Promise<T>;
}

export async function apiFetch(
  token: string | null,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
}
